import { ArrowDownIcon, ArrowUpIcon, ChevronRightIcon, ScrollTextIcon } from 'lucide-react'
import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { PageBody, PageHeader } from '@/components/AppShell'
import { EmptyState, ErrorState, TableSkeleton } from '@/components/states'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatAbsolute } from '@/lib/format'
import { cn } from '@/lib/utils'

import { itemsOf, useStaffAccounts, useStaffAudit, useStaffTeam, type AuditEvent, type AuditFilters } from '../api'
import { LoadMore, StaffGate, StaffLink } from '../ui'

/** Every action the audit log can hold (StaffAuditActions). */
const ACTIONS = [
  'STAFF_GRANT', 'STAFF_REVOKE', 'STAFF_BOOTSTRAP_GRANT', 'BREAKGLASS_GRANT', 'BREAKGLASS_END', 'BREAKGLASS_READ',
  'IMPERSONATION_START', 'IMPERSONATION_END', 'FEEDBACK_TRIAGE', 'ACCESS_DENIED', 'IMPERSONATED_WRITE',
  'ACCOUNT_SUSPEND', 'ACCOUNT_REACTIVATE', 'USER_SUSPEND', 'USER_REACTIVATE', 'PLAN_CHANGE', 'QUOTA_OVERRIDE',
  'ZONE_PAUSE', 'ZONE_UNPAUSE', 'IDENTITY_RELEASE', 'IDENTITY_REISSUE', 'DELETION_CANCEL', 'DELETION_EXPEDITE',
  'VERIFICATION_RESEND', 'BALANCE_CREDIT', 'BALANCE_REFUND', 'REFUND_REQUEST_APPROVE', 'REFUND_REQUEST_REJECT',
  'REFUND_RESOLVE', 'RECHARGE_SETTLE', 'INVOICES_VIEW', 'INVOICE_DOWNLOAD', 'INVOICE_RESEND', 'INVOICE_VOID',
  'INVOICE_RETRY',
]

interface Option {
  value: string
  label: string
  hint?: string
}

interface Filter {
  /** What the input shows. */
  text: string
  /** What is sent: the picked option's id, or the typed text when nothing was picked. */
  value: string
}

const EMPTY_FILTER: Filter = { text: '', value: '' }

const EMPTY = { account: EMPTY_FILTER, actor: EMPTY_FILTER, action: EMPTY_FILTER }

type Draft = typeof EMPTY

/** A text input whose dropdown lists matches by name; picking one fills in the id behind it. */
function Picker({ id, label, filter, onChange, options, loading }: {
  id: string
  label: string
  filter: Filter
  onChange: (f: Filter) => void
  options: Option[]
  loading?: boolean
}) {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const away = (e: MouseEvent) => {
      if (box.current !== null && !box.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', away)
    return () => { document.removeEventListener('mousedown', away) }
  }, [])
  return (
    <div className="relative min-w-0 space-y-1.5" ref={box}>
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        autoComplete="off"
        value={filter.text}
        onFocus={() => { setOpen(true) }}
        onChange={(e) => { onChange({ text: e.target.value, value: e.target.value }); setOpen(true) }}
        onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false) }}
      />
      {open && (options.length > 0 || loading === true) ? (
        <ul
          role="listbox"
          aria-label={`${label} matches`}
          className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-auto rounded-md border border-border bg-popover text-popover-foreground shadow-md"
        >
          {options.length === 0 ? <li className="px-3 py-2 text-xs text-muted-foreground">Searching…</li> : null}
          {options.map((o) => (
            <li key={o.value} role="option" aria-selected={o.value === filter.value}>
              <button
                type="button"
                className="flex w-full flex-col items-start px-3 py-1.5 text-left text-sm hover:bg-muted focus:bg-muted focus:outline-none"
                onClick={() => { onChange({ text: o.label, value: o.value }); setOpen(false) }}
              >
                <span className="max-w-full truncate">{o.label}</span>
                {o.hint === undefined ? null : <span className="max-w-full truncate font-mono text-[11px] text-muted-foreground">{o.hint}</span>}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

function useDebounced(value: string, ms = 250) {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => { setV(value) }, ms)
    return () => { clearTimeout(t) }
  }, [value, ms])
  return v
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function target(e: AuditEvent) {
  if (e.identity_name !== null) return `identity ${e.identity_name}`
  if (e.zone_name !== null) return `zone ${e.zone_name}`
  if (e.user_email !== null) return e.user_email
  if (e.account_name !== null) return e.account_name
  return '—'
}

type SortKey = 'when' | 'actor' | 'action' | 'outcome' | 'target' | 'reason' | 'notified' | 'request'

const SORTERS: Record<SortKey, (e: AuditEvent) => string | number> = {
  when: (e) => e.occurred_at,
  actor: (e) => (e.actor_email ?? 'system').toLowerCase(),
  action: (e) => e.action,
  outcome: (e) => e.outcome,
  target: (e) => target(e).toLowerCase(),
  reason: (e) => (e.reason ?? '').toLowerCase(),
  notified: (e) => (e.notified === null ? -1 : e.notified ? 1 : 0),
  request: (e) => e.request_id ?? '',
}

const COLUMNS: { key: SortKey; label: string; width: number }[] = [
  { key: 'when', label: 'When', width: 170 },
  { key: 'actor', label: 'Actor', width: 220 },
  { key: 'action', label: 'Action', width: 170 },
  { key: 'outcome', label: 'Outcome', width: 110 },
  { key: 'target', label: 'Target', width: 220 },
  { key: 'reason', label: 'Reason', width: 280 },
  { key: 'notified', label: 'Notified', width: 90 },
  { key: 'request', label: 'Request', width: 120 },
]

/** A header cell: click to sort, drag its right edge to widen or narrow the column. */
function Header({ column, width, sort, onSort, onResize }: {
  column: (typeof COLUMNS)[number]
  width: number
  sort: { key: SortKey; dir: 'asc' | 'desc' } | null
  onSort: (key: SortKey) => void
  onResize: (key: SortKey, width: number) => void
}) {
  const active = sort?.key === column.key
  const startDrag = (e: React.PointerEvent<HTMLSpanElement>) => {
    e.preventDefault()
    e.stopPropagation()
    const startX = e.clientX
    const startWidth = width
    const move = (m: PointerEvent) => { onResize(column.key, Math.max(60, startWidth + m.clientX - startX)) }
    const up = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }
  return (
    <TableHead
      style={{ width, minWidth: width, maxWidth: width }}
      className="relative select-none p-0"
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className="flex h-full w-full items-center gap-1 px-3 py-2 text-left font-medium hover:text-foreground"
        onClick={() => { onSort(column.key) }}
      >
        <span className="truncate">{column.label}</span>
        {active ? (sort.dir === 'asc' ? <ArrowUpIcon className="size-3.5 shrink-0" /> : <ArrowDownIcon className="size-3.5 shrink-0" />) : null}
      </button>
      <span
        role="separator"
        aria-orientation="vertical"
        aria-label={`Resize ${column.label}`}
        onPointerDown={startDrag}
        onDoubleClick={() => { onResize(column.key, column.width) }}
        className="absolute right-0 top-0 h-full w-2 cursor-col-resize touch-none border-r border-border hover:border-primary hover:bg-primary/20"
      />
    </TableHead>
  )
}

function pretty(json: string | null): ReactNode {
  if (json === null || json === '') return <span className="text-muted-foreground">—</span>
  try {
    const parsed: unknown = JSON.parse(json)
    if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 text-xs">
          {Object.entries(parsed as Record<string, unknown>).map(([k, v]) => (
            <Fragment key={k}>
              <dt className="text-muted-foreground">{k}</dt>
              <dd className="break-all font-mono">{v === null ? 'null' : typeof v === 'object' ? JSON.stringify(v) : String(v)}</dd>
            </Fragment>
          ))}
        </dl>
      )
    }
  } catch {
    // not JSON: show it as it is
  }
  return <code className="break-all text-xs">{json}</code>
}

export function StaffAuditPage() {
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [applied, setApplied] = useState<Draft>(EMPTY)
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' } | null>(null)
  const [widths, setWidths] = useState<Record<SortKey, number>>(
    () => Object.fromEntries(COLUMNS.map((c) => [c.key, c.width])) as Record<SortKey, number>,
  )
  const [openRow, setOpenRow] = useState<string | null>(null)

  const filters: AuditFilters = {}
  if (applied.account.value.trim() !== '') filters.account_id = applied.account.value.trim()
  if (applied.actor.value.trim() !== '') filters.actor_id = applied.actor.value.trim()
  if (applied.action.value.trim() !== '') filters.action = applied.action.value.trim()
  const list = useStaffAudit(filters, true)
  const loaded = itemsOf(list.data)
  const rows = useMemo(() => {
    if (sort === null) return loaded
    const get = SORTERS[sort.key]
    const sign = sort.dir === 'asc' ? 1 : -1
    return [...loaded].sort((a, b) => {
      const x = get(a)
      const y = get(b)
      return (x < y ? -1 : x > y ? 1 : 0) * sign
    })
  }, [loaded, sort])

  // Account matches: the directory search, only while the user is typing a name (not an id).
  const accountText = useDebounced(draft.account.text.trim())
  const typingAccount = draft.account.text === draft.account.value && accountText.length >= 2 && !UUID.test(accountText)
  const accountHits = useStaffAccounts(accountText, '', typingAccount)
  const accountOptions: Option[] = typingAccount
    ? itemsOf(accountHits.data).slice(0, 12).map((a) => ({ value: a.id, label: a.name, hint: `${a.plan} · ${String(a.member_count)} member(s) · created ${a.created_at.slice(0, 10)} · ${a.id}` }))
    : []

  const team = useStaffTeam(true)
  const needle = (t: string) => t.trim().toLowerCase()
  const actorOptions: Option[] = (team.data ?? [])
    .filter((m) => m.user_id !== null)
    .filter((m) => draft.actor.text === draft.actor.value
      && `${m.email} ${m.name ?? ''}`.toLowerCase().includes(needle(draft.actor.text)))
    .slice(0, 12)
    .map((m) => ({ value: m.user_id as string, label: m.name === null ? m.email : `${m.name} · ${m.email}`, hint: `${m.tier} · ${m.user_id as string}` }))
  const actionOptions: Option[] = ACTIONS
    .filter((a) => draft.action.text === draft.action.value && a.toLowerCase().includes(needle(draft.action.text)))
    .map((a) => ({ value: a, label: a }))

  const onSort = (key: SortKey) => {
    setSort((s) => (s?.key !== key ? { key, dir: 'asc' } : s.dir === 'asc' ? { key, dir: 'desc' } : null))
  }
  const total = COLUMNS.reduce((n, c) => n + widths[c.key], 28)

  return (
    <StaffGate needs="audit_read">
      <PageHeader eyebrow="Staff" title="Audit log" description="Every staff write and break-glass read, append-only." />
      <PageBody>
        <Card>
          <CardContent className="space-y-4">
            <form
              className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
              onSubmit={(e) => {
                e.preventDefault()
                setApplied(draft)
              }}
            >
              <Picker id="aud-acc" label="Account" filter={draft.account} options={accountOptions}
                loading={typingAccount && accountHits.isFetching}
                onChange={(f) => { setDraft((d) => ({ ...d, account: f })) }} />
              <Picker id="aud-actor" label="Actor" filter={draft.actor} options={actorOptions}
                onChange={(f) => { setDraft((d) => ({ ...d, actor: f })) }} />
              <Picker id="aud-action" label="Action" filter={draft.action} options={actionOptions}
                onChange={(f) => { setDraft((d) => ({ ...d, action: f })) }} />
              <div className="flex items-end gap-2">
                <Button type="submit">Apply</Button>
                <Button type="button" variant="ghost" onClick={() => { setDraft(EMPTY); setApplied(EMPTY) }}>Clear</Button>
              </div>
            </form>

            {list.isError ? (
              <ErrorState error={list.error} title="Could not load the audit log" onRetry={() => void list.refetch()} />
            ) : list.isPending ? (
              <TableSkeleton rows={6} columns={6} />
            ) : rows.length === 0 ? (
              <EmptyState icon={ScrollTextIcon} title="No audit events" description="Nothing matches these filters." />
            ) : (
              <>
                <p className="text-xs text-muted-foreground">
                  Click a header to sort the rows loaded so far, drag its edge to resize (double-click resets), click a row for the full record.
                </p>
                <div className="overflow-x-auto">
                  <Table className="table-fixed" style={{ width: total, minWidth: '100%' }}>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-7 p-0" aria-label="Details" />
                        {COLUMNS.map((c) => (
                          <Header key={c.key} column={c} width={widths[c.key]} sort={sort} onSort={onSort}
                            onResize={(key, w) => { setWidths((cur) => ({ ...cur, [key]: w })) }} />
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((e) => {
                        const expanded = openRow === e.audit_id
                        return (
                          <Fragment key={e.audit_id}>
                            <TableRow data-testid="staff-audit-row" className="cursor-pointer"
                              onClick={() => { setOpenRow(expanded ? null : e.audit_id) }}>
                              <TableCell className="w-7 p-0 pl-2">
                                <ChevronRightIcon className={cn('size-4 text-muted-foreground transition-transform', expanded && 'rotate-90')} />
                              </TableCell>
                              <TableCell className="truncate text-xs text-muted-foreground tabular-nums">{formatAbsolute(e.occurred_at)}</TableCell>
                              <TableCell className="truncate text-xs" title={e.actor_email ?? undefined}>
                                {e.actor_email ?? 'system'} {e.actor_tier === null ? null : <Badge variant="secondary">{e.actor_tier}</Badge>}
                                {e.channel === 'mcp' ? <Badge variant="outline" data-testid="staff-audit-channel-mcp">MCP</Badge> : null}
                              </TableCell>
                              <TableCell className="truncate font-mono text-xs">{e.action}</TableCell>
                              <TableCell><Badge variant={e.outcome === 'denied' ? 'destructive' : 'success'}>{e.outcome}</Badge></TableCell>
                              <TableCell className="truncate text-xs" title={target(e)}>
                                {e.account_id === null ? target(e) : (
                                  <span onClick={(ev) => { ev.stopPropagation() }}>
                                    <StaffLink to={`/app/staff/accounts/${e.account_id}`}>{target(e)}</StaffLink>
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="truncate text-xs text-muted-foreground" title={e.reason ?? undefined}>{e.reason ?? '—'}</TableCell>
                              <TableCell className="text-xs">{e.notified === null ? '—' : e.notified ? 'yes' : 'no'}</TableCell>
                              <TableCell className="truncate font-mono text-xs" title={e.request_id ?? undefined}>{e.request_id?.slice(0, 8) ?? '—'}</TableCell>
                            </TableRow>
                            {expanded ? (
                              <TableRow data-testid="staff-audit-detail" className="bg-muted/30 hover:bg-muted/30">
                                <TableCell colSpan={COLUMNS.length + 1} className="whitespace-normal">
                                  <div className="grid gap-4 py-2 md:grid-cols-2">
                                    <div className="space-y-1">
                                      <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Before</h4>
                                      {pretty(e.before)}
                                    </div>
                                    <div className="space-y-1">
                                      <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">After</h4>
                                      {pretty(e.after)}
                                    </div>
                                    <div className="space-y-1 text-xs md:col-span-2">
                                      <p><span className="text-muted-foreground">Reason: </span>{e.reason ?? '—'}</p>
                                      <p><span className="text-muted-foreground">Route: </span><span className="font-mono">{e.route ?? '—'}</span></p>
                                      <p><span className="text-muted-foreground">Request: </span><span className="font-mono">{e.request_id ?? '—'}</span></p>
                                      <p><span className="text-muted-foreground">Account: </span><span className="font-mono">{e.account_id ?? '—'}</span></p>
                                      <p><span className="text-muted-foreground">Actor: </span><span className="font-mono">{e.actor_user_id ?? '—'}</span></p>
                                    </div>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ) : null}
                          </Fragment>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>
                <LoadMore shown={rows.length} hasNext={list.hasNextPage} loading={list.isFetchingNextPage} onMore={() => void list.fetchNextPage()} />
              </>
            )}
          </CardContent>
        </Card>
      </PageBody>
    </StaffGate>
  )
}
